import React, { useState, useEffect, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/contexts/ThemeContext.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faCertificate, faClipboardCheck, faLock, faLeaf } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ISO27001Module from "./modules/ISO27001Module";
import ISO27701Module from "./modules/ISO27701Module";
import RFFRModule from "./modules/RFFRModule";
import DISPModule from "./modules/DISPModule";
import ISO14001Module from "./modules/ISO14001Module";
import Essential8Module from "./modules/Essential8Module";
import PrivacyActModule from "./modules/PrivacyActModule";
import ISO42001Module from "./modules/ISO42001Module";
import ISO45001Module from "./modules/ISO45001Module";
import ISO9001Module from "./modules/ISO9001Module";
import NIST80053Module from "./modules/NIST80053Module";
import NISTCSFModule from "./modules/NISTCSFModule";
import SOC2Module from "./modules/SOC2Module";
import SMB1001Module from "./modules/SMB1001Module";
import companyService from "@/services/companyService";
import { toLocalDateString } from "../../../../../shared/renderDate";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BeautifulTabsSelector } from "@/components/ui/BeautifulTabsSelector";
import { useTenancy } from "@/contexts/TenancyContext";
import { PaywallDialog } from "@/components/PaywallDialog";
// Base module groups without locking logic
const BASE_MODULE_GROUPS = [
  {
    group: "ISO Standards",
    type: "Management Systems",
    options: [
      { value: "iso9001", label: "ISO 9001" },
      { value: "iso14001", label: "ISO 14001" },
      { value: "iso27001", label: "ISO 27001" },
      { value: "iso27701", label: "ISO 27701" },
      { value: "iso42001", label: "ISO 42001" },
      { value: "iso45001", label: "ISO 45001" },
    ],
  },
  {
    group: "NIST Frameworks",
    type: "Security & Privacy",
    options: [
      { value: "nist80053", label: "NIST 800-53" },
      { value: "nistcsf", label: "NIST CSF" },
    ],
  },
  {
    group: "Other Frameworks",
    type: "Security & Compliance",
    options: [
      { value: "disp", label: "DISP" },
      { value: "essential8", label: "Essential 8" },
      { value: "rffr", label: "ISM / RFFR" },
      { value: "privacyAct", label: "Privacy Act" },
      { value: "smb1001", label: "SMB 1001" },
      { value: "soc2", label: "SOC 2" },
    ],
  },
];

/**
 * Determine if a module is locked based on plan and enabled modules
 */
function isModuleLocked(
  moduleValue: string,
  plan: string,
  enabledModules: string[]
): boolean {
  const planLower = plan?.toLowerCase() || '';
  
  // Starter plan: Only Privacy Act, Essential 8, SMB1001, and DISP are unlocked
  if (planLower.includes('starter')) {
    return !['privacyAct', 'essential8', 'smb1001', 'disp'].includes(moduleValue);
  }
  
  // Business plan: All unlocked except ISM/RFFR
  if (planLower.includes('business')) {
    return moduleValue === 'rffr';
  }
  
  // Enterprise: Everything unlocked
  if (planLower.includes('enterprise')) {
    return false;
  }
  
  // Default: lock everything
  return true;
}

/**
 * Determine if a module requires a paywall dialog
 */
function requiresPaywall(
  moduleValue: string,
  plan: string,
  enabledModules: string[]
): boolean {
  const planLower = plan?.toLowerCase() || '';
  
  // DISP requires paywall for Starter and Business (unless already enabled)
  if (moduleValue === 'disp') {
    const isDISPEnabled = enabledModules.some(m => 
      m.toLowerCase() === 'disp' || m === 'disp'
    );
    
    if (isDISPEnabled) {
      return false; // Already enabled, no paywall
    }
    
    return planLower.includes('starter') || planLower.includes('business');
  }
  
  return false;
}

// Export for backward compatibility
export const MODULE_GROUPS = BASE_MODULE_GROUPS;

// Helper to flatten all modules for lookup
export const MODULES = MODULE_GROUPS.flatMap(g => g.options);


function ModulesSection() {
  const { enabledModules, activeTenancy, isModuleEnabled } = useTenancy();
  const [paywallOpen, setPaywallOpen] = useState(false);
  // ISO 27001 state
  const [iso27001, setIso27001] = useState({
    auditFirm: "",
    primaryContact: "",
    auditorName: "",
    stage1Date: "",
    stage1Result: "",
    stage2Date: "",
    stage2Result: "",
    lastAuditDate: "",
    nextAuditDate: "",
    enabled: "disabled", // "enabled" or "disabled"
  });
  const [iso27001Initial, setIso27001Initial] = useState(null);
  const [isLoadingISO, setIsLoadingISO] = useState(true);
  const [isSavingISO, setIsSavingISO] = useState(false);
  // Start with empty string, useEffect will set the correct initial tab
  const [selectedTab, setSelectedTab] = useState<string>("");

  // Generate module groups with locking logic
  const moduleGroupsWithLocking = useMemo(() => {
    const plan = activeTenancy?.plan || '';
    
    return BASE_MODULE_GROUPS.map(group => ({
      ...group,
      options: group.options.map(option => {
        const locked = isModuleLocked(option.value, plan, enabledModules);
        const needsPaywall = requiresPaywall(option.value, plan, enabledModules);
        // Use the isModuleEnabled function from the context for proper checking
        const enabled = isModuleEnabled(option.value);
        
        return {
          ...option,
          disabled: false, // Don't disable, let handleTabChange handle it
          enabled: enabled, // Pass enabled status
          locked: locked,   // Pass locked status
          icon: locked ? <Lock className="w-4 h-4 text-muted-foreground" /> : undefined,
          label: (
            <span className={`flex items-center gap-2 ${locked ? 'opacity-50' : ''}`}>
              {option.label}
              {needsPaywall && !locked && (
                <span className="text-xs text-amber-600 dark:text-amber-400">(Premium)</span>
              )}
              {locked && (
                <span className="text-xs text-muted-foreground">(Locked)</span>
              )}
            </span>
          ),
        };
      }),
    }));
  }, [activeTenancy?.plan, enabledModules]);

  // Ensure at least one of their enabled modules is the first selected tab
  useEffect(() => {
    const plan = activeTenancy?.plan || '';
    
    // Don't change if we already have a valid selection (not locked)
    if (selectedTab) {
      const isCurrentLocked = isModuleLocked(selectedTab, plan, enabledModules);
      if (!isCurrentLocked) return;
    }
    
    // Find the first module that is NOT locked
    // Priority: enabled modules first, then any unlocked module
    const allModuleValues = MODULES.map(m => m.value);
    
    // First try to find an enabled module that's not locked
    for (const moduleValue of allModuleValues) {
      const isLocked = isModuleLocked(moduleValue, plan, enabledModules);
      if (isLocked) continue; // Skip locked modules
      
      const isEnabled = enabledModules.some(em => 
        em.toLowerCase() === moduleValue.toLowerCase() || 
        em === moduleValue ||
        (moduleValue === 'iso27001' && em === 'iso27001-2013')
      );
      
      if (isEnabled) {
        setSelectedTab(moduleValue);
        return;
      }
    }
    
    // If no enabled+unlocked module found, default to first unlocked module for the plan
    if (plan?.toLowerCase().includes('starter')) {
      setSelectedTab('privacyAct'); // Default for starter
    } else if (plan?.toLowerCase().includes('business')) {
      setSelectedTab('iso27001'); // Default for business
    } else if (plan?.toLowerCase().includes('enterprise')) {
      setSelectedTab('iso27001'); // Default for enterprise
    } else {
      // Unknown plan - find first unlocked module
      for (const moduleValue of allModuleValues) {
        if (!isModuleLocked(moduleValue, plan, enabledModules)) {
          setSelectedTab(moduleValue);
          return;
        }
      }
      // Ultimate fallback
      setSelectedTab('privacyAct');
    }
  // Note: selectedTab intentionally not in deps - we only want to run this when enabledModules or plan changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledModules, activeTenancy?.plan]);
  
  // Handle tab change with paywall check
  const handleTabChange = (newTab: string) => {
    const plan = activeTenancy?.plan || '';
    const locked = isModuleLocked(newTab, plan, enabledModules);
    const needsPaywall = requiresPaywall(newTab, plan, enabledModules);
    
    // If module is locked, show paywall dialog
    if (locked) {
      setPaywallOpen(true);
      return;
    }
    
    // If module requires paywall (like DISP), show paywall dialog
    if (needsPaywall) {
      setPaywallOpen(true);
      return;
    }
    
    setSelectedTab(newTab);
  };

  // Utility: Firestore array -> state
  function isoArrayToState(arr, active) {
    const get = (name) => arr?.find(e => e.settingName === name)?.value || "";
    return {
      auditFirm: get("ISO Audit Firm"),
      primaryContact: get("Primary Contact"),
      auditorName: get("Auditor Name"),
      stage1Date: toLocalDateString(get("Stage 1 Date")),
      stage1Result: get("Stage 1 Result"),
      stage2Date: toLocalDateString(get("Stage 2 Date")),
      stage2Result: get("Stage 2 Result"),
      lastAuditDate: toLocalDateString(get("Last Audit Date")),
      nextAuditDate: toLocalDateString(get("Next Audit Date")),
      enabled: active ? "enabled" : "disabled",
    };
  }
  // Utility: state -> Firestore array
  function isoStateToArray(state) {
    // Convert YYYY-MM-DD string to Date for Firestore (or leave as string if empty)
    const parseDate = (str) => str ? new Date(str) : "";
    return [
      { settingName: "ISO Audit Firm", value: state.auditFirm },
      { settingName: "Primary Contact", value: state.primaryContact },
      { settingName: "Auditor Name", value: state.auditorName },
      { settingName: "Stage 1 Date", value: parseDate(state.stage1Date) },
      { settingName: "Stage 1 Result", value: state.stage1Result },
      { settingName: "Stage 2 Date", value: parseDate(state.stage2Date) },
      { settingName: "Stage 2 Result", value: state.stage2Result },
      { settingName: "Last Audit Date", value: parseDate(state.lastAuditDate) },
      { settingName: "Next Audit Date", value: parseDate(state.nextAuditDate) },
    ];
  }

  // Load ISO 27001 settings on mount
  useEffect(() => {
    const fetchISO = async () => {
      setIsLoadingISO(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const isoArr = settings?.["iso27001-2013"] || [];
        const isoActive = settings?.["iso27001-2013"]?.active ?? false;
        // isoArr is an array, isoActive is a boolean
        const arr = Array.isArray(isoArr) ? isoArr : Object.values(isoArr).filter(x => typeof x === 'object' && x.settingName);
        const state = isoArrayToState(arr, isoActive);
        setIso27001(state);
        setIso27001Initial(state);
      } catch (err) {
        console.error("Failed to fetch ISO 27001 settings:", err);

      } finally {
        setIsLoadingISO(false);
      }
    };
    fetchISO();
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">

        <div className="flex flex-row items-center gap-4 mb-4">
          <BeautifulTabsSelector
            options={moduleGroupsWithLocking}
            value={selectedTab}
            onChange={handleTabChange}
          />
        </div>
        
        <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} />

        {/* Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
          {/* ISO 27001 Tab Content */}
          <TabsContent value="iso27001" className="space-y-6">
            <div className="bg-muted/30 p-6 rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-blue-500/10 p-3 rounded-full">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-blue-600" size="lg" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">ISO 27001</h3>
                  <p className="text-sm text-muted-foreground">Information Security Management System</p>
                </div>
              </div>
              <ISO27001Module />
            </div>
          </TabsContent>
          {/* ISO 27701 Tab Content */}
          <TabsContent value="iso27701" className="space-y-6">
            <div className="bg-muted/30 p-6 rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-orange-500/10 p-3 rounded-full">
                  <FontAwesomeIcon icon={faClipboardCheck} className="text-orange-600" size="lg" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">ISO 27701</h3>
                  <p className="text-sm text-muted-foreground">Privacy Information Management System</p>
                </div>
              </div>
              <ISO27701Module />
            </div>
          </TabsContent>

          {/* ISM / RFFR Tab Content */}
          <TabsContent value="rffr" className="space-y-6">
            <div className="bg-muted/30 p-6 rounded-lg border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-green-500/10 p-3 rounded-full">
                  <FontAwesomeIcon icon={faLock} className="text-green-600" size="lg" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground">ISM / RFFR</h3>
                  <p className="text-sm text-muted-foreground">Information Security Manual & Right Fit for Risk</p>
                </div>
              </div>
              <RFFRModule />
            </div>
          </TabsContent>

          {/* Essential 8 Tab Content */}
          <TabsContent value="essential8" className="space-y-6">
            <Essential8Module />
          </TabsContent>

          {/* DISP Tab Content */}
          <TabsContent value="disp" className="space-y-6">
            <DISPModule />
          </TabsContent>
          {/* ISO 14001 Tab Content */}
          <TabsContent value="iso14001" className="space-y-6">
            <ISO14001Module />
          </TabsContent>

          {/* Privacy Act Tab Content */}
          <TabsContent value="privacyAct" className="space-y-6">
            <PrivacyActModule />
          </TabsContent>

          {/* ISO 42001 Tab Content */}
          <TabsContent value="iso42001" className="space-y-6">
            <ISO42001Module />
          </TabsContent>

          {/* ISO 45001 Tab Content */}
          <TabsContent value="iso45001" className="space-y-6">
            <ISO45001Module />
          </TabsContent>

          {/* ISO 9001 Tab Content */}
          <TabsContent value="iso9001" className="space-y-6">
            <ISO9001Module />
          </TabsContent>

          {/* SOC 2 Tab Content */}
          <TabsContent value="soc2" className="space-y-6">
            <SOC2Module />
          </TabsContent>

          {/* NIST 800-53 Tab Content */}
          <TabsContent value="nist80053" className="space-y-6">
            <NIST80053Module />
          </TabsContent>

          {/* NIST CSF Tab Content */}
          <TabsContent value="nistcsf" className="space-y-6">
            <NISTCSFModule />
          </TabsContent>

          {/* SMB 1001 Tab Content */}
          <TabsContent value="smb1001" className="space-y-6">
            <SMB1001Module />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default ModulesSection;
